import { airconSections as aeAircon, ptSections as aePt } from '../../data/markets/ae/sections'
import { airconSections as abuDhabiAircon, ptSections as abuDhabiPt } from '../../data/markets/ae/sections-abu-dhabi'
import { airconSections as myAircon, ptSections as myPt } from '../../data/markets/my/sections'
import { AIRCON_SERVICES, PT_TOPICS, marketKey } from '../../data/markets/registry'
import { airconHubPath, ptHubPath } from './content'

// Copy and link data for the shared Singapore-style sections on the UAE /
// Malaysia pages (AirconServices, AirconServiceMatcher, WhyUs, HowToBook,
// PersonalTrainerWhyUs, TrainerValueProposition, ...). The prose lives in
// data/markets/<cc>/sections.js; this module adds the parts built from the
// page registry (links), so a page added to the registry appears here too.

// Keyed by country, or `country/city` where a city needs its own wording
// (Abu Dhabi: same language and prices as Dubai, so shared copy would make
// its pages near-duplicates of Dubai's).
const AIRCON_COPY = { ae: aeAircon, 'ae/abu-dhabi': abuDhabiAircon, my: myAircon }
const PT_COPY = { ae: aePt, 'ae/abu-dhabi': abuDhabiPt, my: myPt }
const copyFor = (table, ctx) => table[marketKey(ctx.market.countryCd, ctx.city.slug)] ?? table[ctx.market.countryCd]

// Emoji for the services grid, by slug keyword (same style as the SG grid).
const SERVICE_ICONS = [
    [/price|rates|harga/, '💲'],
    [/gas/, '❄️'],
    [/repair/, '🛠️'],
    [/not-cool|not-cold|tak-sejuk/, '🌡️'],
    [/leak/, '💧'],
    [/overhaul|deep/, '🧪'],
    [/chemical|coil/, '🧼'],
    [/contract|package|amc/, '📅'],
    [/ducted|cassette/, '📦'],
    [/cleaning|general/, '🧾'],
]
const iconFor = (slug) => SERVICE_ICONS.find(([re]) => re.test(slug))?.[1] ?? '🔧'

const GOAL_ICONS = [
    [/weight-loss/, '🔥'],
    [/female/, '👩'],
    [/freelance/, '🤝'],
    [/senior|elderly/, '🧓'],
    [/natal/, '🤰'],
    [/price|rates/, '💲'],
]
const goalIconFor = (slug) => GOAL_ICONS.find(([re]) => re.test(slug))?.[1] ?? '💪'

const sectionContext = (ctx, year) => ({
    ...ctx,
    year,
    base: ctx.city ? airconHubPath(ctx.market, ctx.city.slug) : null,
    ptBase: ctx.city ? ptHubPath(ctx.market, ctx.city.slug) : null,
})

export const getAirconSections = (ctx, year = new Date().getFullYear()) => {
    const sctx = sectionContext(ctx, year)
    const copy = copyFor(AIRCON_COPY, ctx)(sctx)
    const pages = AIRCON_SERVICES[marketKey(ctx.market.countryCd, ctx.city.slug)] ?? []
    const slugs = new Set(pages.map((p) => p.slug))
    const pricePage = pages.find((p) => /price/.test(p.slug))
    return {
        ...copy,
        priceHref: pricePage ? `${sctx.base}/${pricePage.slug}` : null,
        localityLinks: ctx.city.localities.map((l) => ({ label: l.name, href: `${sctx.base}/locality/${l.slug}` })),
        serviceCards: pages.map((p) => ({ title: p.title, desc: p.blurb, href: `${sctx.base}/${p.slug}`, icon: iconFor(p.slug) })),
        // Rows whose page this city does not have link to the hub instead.
        matcherRows: copy.matcher.rows.map((row) => ({
            ...row,
            href: slugs.has(row.slug) ? `${sctx.base}/${row.slug}` : sctx.base,
        })),
    }
}

export const getPtSections = (ctx, year = new Date().getFullYear()) => {
    const sctx = sectionContext(ctx, year)
    const copy = copyFor(PT_COPY, ctx)(sctx)
    const topics = PT_TOPICS[marketKey(ctx.market.countryCd, ctx.city.slug)] ?? []
    return {
        ...copy,
        goals: topics.map((p) => ({ goal: p.title, icon: goalIconFor(p.slug), link: `${sctx.ptBase}/${p.slug}`, desc: p.blurb })),
    }
}
